// Crypto icon mapping - returns emoji/symbol for popular cryptocurrencies
export const getCryptoIcon = (symbol) => {
  const icons = {
    // Major cryptocurrencies
    'BTC': '₿',
    'Bitcoin': '₿',
    'ETH': 'Ξ',
    'Ethereum': 'Ξ',
    'USDT': '₮',
    'Tether': '₮',
    'USDC': '$',
    'USD Coin': '$',
    'BNB': '🔶',
    'Binance Coin': '🔶',
    'XRP': '✕',
    'Ripple': '✕',
    'ADA': '₳',
    'Cardano': '₳',
    'SOL': '◎',
    'Solana': '◎',
    'DOGE': 'Ð',
    'Dogecoin': 'Ð',
    'DOT': '●',
    'Polkadot': '●',
    'MATIC': '▲',
    'Polygon': '▲',
    'DAI': '◈',
    'SHIB': '🐕',
    'Shiba Inu': '🐕',
    'TRX': '⬢',
    'TRON': '⬢',
    'UNI': '🦄',
    'Uniswap': '🦄',
    'AVAX': '🔺',
    'Avalanche': '🔺',
    'WBTC': '₿',
    'Wrapped Bitcoin': '₿',
    'ATOM': '⚛',
    'Cosmos': '⚛',
    'LTC': 'Ł',
    'Litecoin': 'Ł',
    'LINK': '⬡',
    'Chainlink': '⬡',
    'BCH': '₿',
    'Bitcoin Cash': '₿',
    'NEAR': '🌐',
    'ALGO': '🔷',
    'Algorand': '🔷',
    'XLM': '🚀',
    'Stellar': '🚀',
    'VET': '♦',
    'VeChain': '♦',
    'ICP': '∞',
    'Internet Computer': '∞',
    'FIL': '⬢',
    'Filecoin': '⬢',
    'APT': '🔴',
    'Aptos': '🔴',
    'ARB': '🔵',
    'Arbitrum': '🔵',
    'OP': '🔴',
    'Optimism': '🔴',
    'HBAR': 'ℏ',
    'Hedera': 'ℏ',
    'XMR': 'ɱ',
    'Monero': 'ɱ',
    'GRT': '📊',
    'The Graph': '📊',
    'SAND': '🏖',
    'The Sandbox': '🏖',
    'MANA': '🌐',
    'Decentraland': '🌐',
    'AXS': '🎮',
    'Axie Infinity': '🎮',
    'AAVE': '👻',
    'CRV': '🌊',
    'Curve': '🌊',
    'MKR': '📐',
    'Maker': '📐',
    'COMP': '🏛',
    'Compound': '🏛',
    'SNX': '💎',
    'Synthetix': '💎',
    'FTM': '👻',
    'Fantom': '👻',
    'ZEC': 'ⓩ',
    'Zcash': 'ⓩ',
    'EOS': '◉',
    'XTZ': 'ꜩ',
    'Tezos': 'ꜩ',
    'THETA': 'Θ',
    'CHZ': '⚽',
    'Chiliz': '⚽',
    'ENJ': '🎮',
    'Enjin': '🎮',
    'SUSHI': '🍣',
    'SushiSwap': '🍣',
    '1INCH': '🦄',
    'YFI': '🏛',
    'Yearn': '🏛',
    'BAT': '🦇',
    'Basic Attention': '🦇',
    'ZRX': '0x',
    'CAKE': '🥞',
    'PancakeSwap': '🥞',
    'LUNA': '🌙',
    'Terra': '🌙',
    'LUNC': '🌙',
    'Terra Classic': '🌙',
    
    // Stablecoins
    'USD': '$',
    'BUSD': '$',
    'UST': '$',
    'TUSD': '$',
    'USDP': '$',
    'GUSD': '$',
    'FRAX': '$',
    'LUSD': '$',
    'USDD': '$',
    
    // Default for unknown
    'DEFAULT': '◉'
  };
  
  // Try exact match first
  if (icons[symbol]) {
    return icons[symbol];
  }
  
  // Try uppercase version
  const upperSymbol = symbol?.toUpperCase();
  if (icons[upperSymbol]) {
    return icons[upperSymbol];
  }
  
  // Fallback to first 2 letters
  return null;
};

// Get icon with fallback to letters
export const getCryptoIconOrLetters = (symbol, name) => {
  const icon = getCryptoIcon(symbol) || getCryptoIcon(name);
  if (icon) {
    return { type: 'icon', value: icon };
  }
  
  // Fallback to first 2 letters
  const letters = (symbol || name || 'CR').slice(0, 2).toUpperCase();
  return { type: 'letters', value: letters };
};

// Get background gradient for crypto
export const getCryptoGradient = (symbol) => {
  const gradients = {
    'BTC': 'linear-gradient(135deg, #f7931a 0%, #f79319 100%)',
    'ETH': 'linear-gradient(135deg, #627eea 0%, #4e5ed4 100%)',
    'USDT': 'linear-gradient(135deg, #26a17b 0%, #22916d 100%)',
    'USDC': 'linear-gradient(135deg, #2775ca 0%, #2467b8 100%)',
    'BNB': 'linear-gradient(135deg, #f3ba2f 0%, #f0b90b 100%)',
    'XRP': 'linear-gradient(135deg, #23292f 0%, #1a1f24 100%)',
    'ADA': 'linear-gradient(135deg, #0033ad 0%, #002d9c 100%)',
    'SOL': 'linear-gradient(135deg, #00ffa3 0%, #dc1fff 100%)',
    'DOGE': 'linear-gradient(135deg, #c2a633 0%, #ba9f33 100%)',
    'DOT': 'linear-gradient(135deg, #e6007a 0%, #d40069 100%)',
    'MATIC': 'linear-gradient(135deg, #8247e5 0%, #7339d5 100%)',
    'AVAX': 'linear-gradient(135deg, #e84142 0%, #d73738 100%)',
    'LINK': 'linear-gradient(135deg, #2a5ada 0%, #2451c7 100%)',
    'ATOM': 'linear-gradient(135deg, #2e3148 0%, #1e2030 100%)',
    'LTC': 'linear-gradient(135deg, #bfbbbb 0%, #a8a4a4 100%)',
    'UNI': 'linear-gradient(135deg, #ff007a 0%, #e6006a 100%)',
    'ALGO': 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
    'XLM': 'linear-gradient(135deg, #000000 0%, #14b6e7 100%)',
    'DEFAULT': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  
  const upperSymbol = symbol?.toUpperCase();
  return gradients[upperSymbol] || gradients.DEFAULT;
};